import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Icon, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme, Card, CardContent, CardActions, Button, Chip } from '@mui/material';

import { ToolsListing } from '../../components/custom';
import { useAuthContext } from '../../context';
import { LayoutBase } from '../../layouts';
import { IUser, UserServices } from '../../services/api/users/UserServices';
import { Loadin } from '../../components/shared';
import { CustomAlert } from '../../components/shared/VAlert';
import { ConfirmationDialog } from '../../components/shared/VConfirmationDialog';
import { SupervisedUserCircle, Edit, Delete } from '@mui/icons-material';

export const ListUsers: React.FC = () => {
    const navigate = useNavigate();
    const { userData } = useAuthContext();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<IUser[]>([]);
    const [dataToSearch, setDataToSearch] = useState<IUser[]>([]);
    const [searchText, setSearchText] = useState('');
    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [userIdToDelete, setUserIdToDelete] = useState<string | null>(null);

    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));

    const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setAlertInfo({ open: false, message: '', severity: 'info' });
        setTimeout(() => {
            setAlertInfo({ open: true, message, severity });
        }, 100);
    };

    const handleOpenDeleteDialog = (uuid: string) => {
        setUserIdToDelete(uuid);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setUserIdToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (userIdToDelete) {
            UserServices.deleteById(userIdToDelete)
                .then(result => {
                    if (result instanceof Error) {
                        showAlert('Não foi possível excluir o usuário!', 'error');
                    } else {
                        showAlert('Usuário excluído!', 'success');
                        setData(data.filter(user => user.uuid !== userIdToDelete));
                    }
                });
        }
        handleCloseDialog();
    };

    useEffect(() => {
        const fetchUsers = async () => {
            setIsLoading(true);
            try {
                const result = await UserServices.get(userData.main_user ? userData.id_empresa : userData.id_empresa);
                if (result instanceof Error) {
                    showAlert('Não foi possível listar os usuários!', 'error');
                } else {
                    if (userData.nivel === 'user') {
                        const filteredData = result.filter(user => user.ativo);
                        setData(filteredData);
                        setDataToSearch(filteredData);
                    } else {
                        setData(result);
                        setDataToSearch(result);
                    }
                }
            } catch (error) {
                showAlert('Ocorreu um erro ao buscar os usuários.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchUsers();
    }, [userData]);

    useEffect(() => {
        if (dataToSearch.length > 0) { 
            if (searchText !== '') {
                const filteredData = dataToSearch.filter(index =>
                    index.nome.toLowerCase().includes(searchText.toLowerCase())
                );
                setData(filteredData);
            } else {
                setData(dataToSearch); 
            }
        }
    }, [searchText, dataToSearch]); 

    const MobileUserCard = ({ row }: { row: IUser }) => (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: '15px' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="div" sx={{ wordBreak: 'break-word', flex: 1, mr: 1 }}>
                        {row.main_user && <SupervisedUserCircle sx={{ verticalAlign: 'middle', mr: 0.5 }} />}
                        {row.nome}
                    </Typography>
                    {userData.nivel !== 'user' && (
                        <Chip
                            label={row.ativo === true ? 'Ativo' : 'Desativado'}
                            size="small"
                            color={row.ativo === true ? 'success' : 'error'}
                            variant="outlined"
                        />
                    )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Email: {row.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Nível: {row.nivel}
                </Typography>
                {row.mobile_phone && (
                    <Typography variant="body2" color="text.secondary">
                        Celular: {row.mobile_phone}
                    </Typography>
                )}
            </CardContent>

            <CardActions sx={{ flexDirection: 'column', gap: 1, p: 2, pt: 0 }}>
                {(userData.permissoes.includes('edit') || userData.permissoes.includes('remove')) && (
                    <Box display="flex" gap={1} justifyContent="center" width="100%">
                        {userData.permissoes.includes('edit') && (
                            <Button
                                startIcon={<Edit />}
                                onClick={() => navigate(`/users/detalhes/${row.uuid}`)}
                                size="small"
                                variant="outlined"
                                color="secondary"
                                fullWidth
                            >
                                Editar
                            </Button>
                        )}
                        {userData.permissoes.includes('remove') && (
                            <Button
                                color='error'
                                startIcon={<Delete />}
                                onClick={() => row.uuid && handleOpenDeleteDialog(row.uuid)}
                                size="small"
                                variant="outlined"
                                fullWidth
                                disabled={row.ativo === false}
                            >
                                Excluir
                            </Button>
                        )}
                    </Box>
                )}
            </CardActions>
        </Card>
    );

    return (
        <LayoutBase
            title='Usuários'
            tollBar={
                <ToolsListing
                    textoBotaoNovo='Novo'
                    showNewButton={userData.permissoes.includes('add')}
                    mostrarInputBusca
                    onClickNew={() => navigate('/users/detalhes/novo')}
                    aoMudarTextoBusca={texto => setSearchText(texto)}
                    textoDaBusca={searchText}
                />
            }
        >
            <Box width={'100%'}>
                {isLoading ?
                    <Loadin />
                    :
                    smDown ? (
                        <Box px={2} pt={2}>
                            {data.length > 0 ? (
                                data.map((row) => (
                                    <MobileUserCard key={row.uuid} row={row} />
                                ))
                            ) : (
                                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '15px' }}>
                                    <Typography color="text.secondary">
                                        Nenhum usuário encontrado
                                    </Typography>
                                </Paper>
                            )}
                        </Box>
                    ) : (
                        <Box padding={2}>
                            <TableContainer component={Paper} variant='outlined' sx={{ borderRadius: '25px' }} >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>E-mail</TableCell>
                                            <TableCell>Nível</TableCell>
                                            <TableCell>Celular</TableCell>
                                            <TableCell>Ações</TableCell>
                                            {userData.nivel !== 'user' ? <TableCell>Status</TableCell> : null}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.map(row => (
                                            <TableRow key={row.uuid}>
                                                <TableCell >{row.main_user ? <SupervisedUserCircle sx={{ verticalAlign: 'middle', mr: 0.5 }} /> : null}<Typography variant='subtitle2' component="span">{row.nome}</Typography></TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.nivel}</TableCell>
                                                <TableCell>{row.mobile_phone}</TableCell>
                                                <TableCell>
                                                    {userData.permissoes.includes('edit') ?
                                                        <IconButton size='small' onClick={() => navigate(`/users/detalhes/${row.uuid}`)}>
                                                            <Icon color='primary'>edit</Icon>
                                                        </IconButton>
                                                        : null
                                                    }
                                                    {userData.permissoes.includes('remove') ?
                                                        <IconButton size='small' onClick={() => row.uuid && handleOpenDeleteDialog(row.uuid)} disabled={row.ativo === false}>
                                                            <Icon color={row.ativo === false ? 'disabled' : 'error'}>delete</Icon>
                                                        </IconButton>
                                                        : null
                                                    }
                                                </TableCell>
                                                {userData.nivel !== 'user' ? <TableCell>{row.ativo == true ? 'Ativo' : 'Desativado'}</TableCell> : null}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )
                }
                {alertInfo.open &&
                    <CustomAlert
                        message={alertInfo.message}
                        severity={alertInfo.severity}
                    />
                }
                <ConfirmationDialog
                    isOpen={dialogOpen}
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirmDelete}
                    title="Confirmação de Exclusão"
                    message="Tem certeza que deseja excluir este usuário?"
                />
            </Box>
        </LayoutBase>
    );
};